import { type Carrier, isCarrier } from "../carriers/carrier.ts";
import type { Derivation, Requirement } from "../requirements.ts";

/**
 * The agency layer (docs/self-improvement.md#agency-rules): one agency's own rules, kept apart from
 * the carriers' so that what one agency prefers never becomes every agency's.
 *
 * Compiled in for now (phase 1a, #186); phase 1b stores them as versioned data read at run time.
 */
type RuleBase = {
  /** Stable and unique in the policy; a value it sets names it as its source. */
  readonly id: string;
  /** The carrier it applies to, by name or alias; `*` for every carrier that declares its keys. */
  readonly carrier: string;
};

/**
 * Which quotes a value rule applies to. `preliminary`: a default for what nothing else gives, which a
 * final quote asks (facts about the client). `always`: the agency's policy at every stage, settled
 * rather than assumed (choices the agency makes, such as the effective date).
 */
export type RuleStage = "preliminary" | "always";

type ValueRule = RuleBase & {
  /** The requirement key it gives a value for; its carrier must declare it. */
  readonly key: string;
  /** Why, shown to the producer with the value. */
  readonly basis: string;
  readonly stage: RuleStage;
};

export type AgencyRule =
  | (ValueRule & { readonly kind: "default"; readonly value: unknown })
  | (ValueRule & {
      readonly kind: "derive";
      /** Tried in order until one gives a value. */
      readonly derive: Derivation | readonly Derivation[];
      /** A year never earlier than another requirement's. */
      readonly notBefore?: string;
    })
  /** Where the values of these keys are looked for, in order, in place of the carrier's. */
  | (RuleBase & {
      readonly kind: "source-order";
      readonly keys: readonly string[];
      readonly lookIn: string;
    })
  /** A choice made in the carrier's portal, and the goal behind it. */
  | (RuleBase & { readonly kind: "strategy"; readonly text: string })
  /** A short note that fits no other kind. */
  | (RuleBase & { readonly kind: "guidance"; readonly text: string });

export type AgencyPolicy = {
  /** The partner's key in `apps/release/partners` (`cgm`). */
  readonly partner: string;
  readonly agency: string;
  /**
   * The agency's time zone (IANA, such as `America/Chicago`): a quote's dates ("tomorrow", "two
   * weeks from today", "within 60 days") are its calendar's, not the UTC day's (#199).
   */
  readonly timeZone: string;
  readonly rules: readonly AgencyRule[];
  /** The CRMs its producers pull a client's loan from (the crm-pull skill). */
  readonly crms: readonly Pick<Carrier, "name" | "aliases">[];
};

/** A rule given as text (`strategy`, `guidance`), as a carrier carries it to the check. */
export type PolicyNote = {
  readonly id: string;
  readonly kind: "strategy" | "guidance";
  readonly text: string;
};

const everyCarrier = (rule: AgencyRule) => rule.carrier === "*";
const appliesTo = (rule: AgencyRule, carrier: Carrier) =>
  everyCarrier(rule) || isCarrier(carrier, rule.carrier);
const keysOf = (rule: AgencyRule): readonly string[] =>
  rule.kind === "source-order" ? rule.keys : "key" in rule ? [rule.key] : [];

/** The calendar day (YYYY-MM-DD) in a time zone at a moment, now by default. */
export function dayIn(timeZone: string, at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(at);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** Refuses a rule that cannot apply as written, and a time zone that is not one. */
function validate(carriers: readonly Carrier[], policy: AgencyPolicy): void {
  try {
    dayIn(policy.timeZone);
  } catch {
    throw new Error(`${policy.partner}'s time zone ${policy.timeZone} is not an IANA time zone`);
  }
  const ids = new Set<string>();
  const targets = new Set<string>();
  for (const rule of policy.rules) {
    const fail = (why: string): never => {
      throw new Error(`${policy.partner}'s rule ${rule.id}: ${why}`);
    };
    if (ids.has(rule.id)) fail("its id is used twice");
    ids.add(rule.id);
    const isValue = rule.kind === "default" || rule.kind === "derive";
    if (everyCarrier(rule) && isValue) fail("a default names one carrier");
    const matched = carriers.filter((c) => appliesTo(rule, c));
    if (!everyCarrier(rule) && matched.length === 0) fail(`no carrier named ${rule.carrier}`);
    for (const key of keysOf(rule)) {
      const declaring = matched.filter((c) => c.requirements?.some((r) => r.key === key));
      if (declaring.length === 0)
        fail(
          everyCarrier(rule)
            ? `no carrier has a requirement ${key}`
            : `${matched[0]?.name} has no requirement ${key}`,
        );
      for (const c of declaring) {
        const r = c.requirements?.find((x) => x.key === key);
        if (r?.need === "in-portal") fail(`${key} is asked only in the portal`);
        const target = `${rule.kind === "source-order" ? "look" : "value"}:${c.name}:${key}`;
        if (targets.has(target)) fail(`a second rule for ${c.name}'s ${key}`);
        targets.add(target);
      }
    }
  }
}

/**
 * The carriers with the agency's rules in place of their own: a `preliminary` value rule becomes
 * the requirement's default (`assume`), an `always` one its `policy`, a `source-order` its look-in,
 * and the text rules the carrier's notes. A producer's override, a confirmed value and the
 * carrier's still win (requirements.ts). Throws on a rule that names no carrier given, a key no
 * carrier it names declares, a value asked only in the portal, or a second rule for one target.
 */
export function applyAgencyPolicy(carriers: readonly Carrier[], policy: AgencyPolicy): Carrier[] {
  validate(carriers, policy);
  return carriers.map((carrier) => {
    const rules = policy.rules.filter((rule) => appliesTo(rule, carrier));
    if (rules.length === 0) return carrier;
    const requirements = carrier.requirements?.map((r): Requirement => {
      let next = r;
      for (const rule of rules) {
        if (rule.kind === "source-order" && rule.keys.includes(r.key))
          next = { ...next, lookIn: rule.lookIn };
        if ((rule.kind === "default" || rule.kind === "derive") && rule.key === r.key) {
          const value =
            rule.kind === "default"
              ? { value: rule.value }
              : { derive: rule.derive, ...(rule.notBefore ? { notBefore: rule.notBefore } : {}) };
          next =
            rule.stage === "preliminary"
              ? { ...next, assume: { ...value, basis: rule.basis } }
              : {
                  ...next,
                  policy: { ...value, basis: rule.basis, source: `agency policy: ${rule.id}` },
                };
        }
      }
      return next;
    });
    const notes: PolicyNote[] = rules.flatMap((rule) =>
      rule.kind === "strategy" || rule.kind === "guidance"
        ? [{ id: rule.id, kind: rule.kind, text: rule.text }]
        : [],
    );
    return {
      ...carrier,
      ...(requirements ? { requirements } : {}),
      ...(notes.length > 0 ? { notes } : {}),
    };
  });
}

/**
 * The policy as `get_context` lists it (docs/self-improvement.md#agency-rules): the rules an agent
 * follows by reading them, before choosing carriers or working in a portal, and the values the
 * policy settles at every stage (`always`), which are never asked. A preliminary default is left
 * out: the check shows each with its rule as the basis.
 */
export function policyText(policy: AgencyPolicy): string {
  const where = (rule: AgencyRule) => (rule.carrier === "*" ? "every carrier" : rule.carrier);
  const lines = policy.rules.flatMap((rule) => {
    if (rule.kind === "source-order")
      return [`- ${rule.id} (${where(rule)}): look for ${rule.keys.join(", ")} in ${rule.lookIn}`];
    if (rule.kind === "strategy" || rule.kind === "guidance")
      return [`- ${rule.id} (${where(rule)}, ${rule.kind}): ${rule.text}`];
    if (rule.stage === "always")
      return [`- ${rule.id} (${where(rule)}, settled, not asked): [${rule.key}] ${rule.basis}`];
    return [];
  });
  return [
    `Agency policy (${policy.agency}), to follow unless the producer says otherwise; check_quote applies its values:`,
    ...lines,
  ].join("\n");
}
